import { InputBase as BaseTextField, Box, FormHelperText, SxProps, Typography, styled } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import React, { ReactNode } from 'react';
import { Spacer } from '../Spacer';
import BkToolTip from 'src/components/tooltip/BkToolTip';

interface Props {
  id: string;
  name: string;
  error?: string;
  helperText?: string;
  value: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onClick?: (e: React.MouseEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onCut?: (e: React.ClipboardEvent<HTMLInputElement>) => void;
  onCopy?: (e: React.ClipboardEvent<HTMLInputElement>) => void;
  onPaste?: (e: React.ClipboardEvent<HTMLInputElement>) => void;
  placeholder?: string;
  label?: string | ReactNode;
  required?: boolean;
  autoFocus?: boolean;
  sx?: SxProps;
  containerSX?: SxProps;
  endAdornment?: ReactNode;
  inputRef?: React.MutableRefObject<HTMLDivElement | null> | ((el: HTMLDivElement | null) => void);
  type?: string;
  disabled?: boolean;
  labelTooltip?: string;
  minRows?: string | number | undefined;
  inputProps?: any;
  autoComplete?: string;
  removeSpacer?: boolean;
  hideHelperText?: boolean;
}

const Container = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: start;
  width: 100%;
  gap: 0.25rem;
`;

export const TextField: React.FC<Props> = ({
  id,
  name,
  error,
  type,
  value,
  onChange,
  onClick,
  onKeyDown,
  onBlur,
  onCut,
  onCopy,
  onPaste,
  label,
  placeholder,
  required = false,
  autoFocus = false,
  sx = {},
  containerSX = {},
  endAdornment,
  inputRef,
  disabled,
  labelTooltip,
  minRows,
  autoComplete,
  hideHelperText,
  removeSpacer = false,
  inputProps = {},
  helperText,
}) => {
  return (
    <Container sx={containerSX}>
      {label && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            sx={{
              fontSize: '0.875rem',
              fontWeight: 600,
              fontFamily: 'Instrument Sans',
              lineHeight: '1.25rem',
              color: error ? '#AC3737' : '#45464F',
            }}
          >
            {label}
            {required ? '*' : null}
          </Typography>
          {labelTooltip && <BkToolTip Icon={InfoOutlinedIcon} title={labelTooltip} />}
        </Box>
      )}
      <BaseTextField
        margin="none"
        fullWidth
        id={id}
        name={name}
        error={!!error}
        value={value}
        placeholder={placeholder}
        onClick={onClick}
        onChange={onChange}
        onKeyDown={onKeyDown}
        onBlur={onBlur}
        onCut={onCut}
        onCopy={onCopy}
        onPaste={onPaste}
        multiline={minRows ? true : undefined}
        autoComplete={autoComplete}
        minRows={minRows}
        autoFocus={autoFocus}
        disabled={disabled}
        type={type}
        inputProps={inputProps}
        sx={{
          borderRadius: '0.375rem',
          border: error ? '0.0625rem solid #AC3737' : '0.0625rem solid #7E828B',
          '& .MuiInputBase-input': {
            borderRadius: '0.375rem',
            fontSize: '1rem',
            fontWeight: 400,
            fontFamily: 'Instrument Sans',
            lineHeight: '1.5rem',
            padding: '0.75rem 1rem',
          },
          ...sx,
        }}
        endAdornment={endAdornment}
        inputRef={inputRef}
      />
      {error && !hideHelperText ? (
        <FormHelperText
          sx={{
            color: '#AC3737',
            marginTop: 0,
            fontFamily: 'Instrument Sans',
            fontSize: '0.875rem',
            lineHeight: '1rem',
            height: '1.5rem',
          }}
        >
          {error}
        </FormHelperText>
      ) : helperText && !error ? (
        <FormHelperText
          sx={{
            marginTop: 0,
            fontFamily: 'Instrument Sans',
            fontSize: '0.875rem',
            lineHeight: '1rem',
            height: '1.5rem',
          }}
        >
          {helperText}
        </FormHelperText>
      ) : removeSpacer ? null : (
        <Spacer h={24} />
      )}
    </Container>
  );
};
